// Terms of Use, report and block — same data as the iOS app (Terms.swift,
// Safety.swift).
//
//  • Terms: users/{uid}/termsVersion + termsAcceptedAt. Anyone who hasn't
//    agreed to TERMS_VERSION sees the agreement screen once.
//  • Block: blocks/{me}/{them} = timestamp. Their rides and requests are
//    hidden for you, and the server skips notifications between you. Only you
//    can read your list; they aren't told.
//  • Report: reports/{id} = { reporter, reported, reason, note?, contextType,
//    contextId, createdAt }. onReportCreated pushes it to the app owner.

import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, onValue, push, remove, set, update, serverTimestamp } from 'firebase/database';

/** Keep in step with Terms.version on iOS. Bump when everyone should agree again. */
export const TERMS_VERSION = '2026-10-19';
export const TERMS_URL     = 'https://falcon-ride.com/terms.html';
export const PRIVACY_URL   = 'https://falcon-ride.com/privacy.html';

export function termsAcceptanceFields() {
  return { termsVersion: TERMS_VERSION, termsAcceptedAt: serverTimestamp() };
}

export function recordTermsAcceptance(uid) {
  return update(ref(getDatabase(), `users/${uid}`), termsAcceptanceFields());
}

/**
 * true once we know the signed-in user hasn't agreed to the current terms.
 * Keeps listening, so the sign-up write (a moment after the account exists)
 * clears it instead of racing it.
 */
export function useNeedsTerms() {
  const [needs, setNeeds] = useState(false);
  useEffect(() => {
    let stopValue = null;
    const stopAuth = onAuthStateChanged(getAuth(), user => {
      if (stopValue) { stopValue(); stopValue = null; }
      setNeeds(false);
      if (!user) return;
      stopValue = onValue(ref(getDatabase(), `users/${user.uid}/termsVersion`), snap => {
        setNeeds(snap.val() !== TERMS_VERSION);
      }, () => setNeeds(false));
    });
    return () => { stopAuth(); if (stopValue) stopValue(); };
  }, []);
  return needs;
}

/* ── Block list ─────────────────────────────────────────────── */

/** Live set of user IDs the signed-in user has blocked. */
export function useBlockedUsers() {
  const [blocked, setBlocked] = useState(() => new Set());
  useEffect(() => {
    let stopValue = null;
    const stopAuth = onAuthStateChanged(getAuth(), user => {
      if (stopValue) { stopValue(); stopValue = null; }
      setBlocked(new Set());
      if (!user) return;
      stopValue = onValue(ref(getDatabase(), `blocks/${user.uid}`), snap => {
        setBlocked(new Set(Object.keys(snap.val() || {})));
      }, () => {});
    });
    return () => { stopAuth(); if (stopValue) stopValue(); };
  }, []);
  return blocked;
}

export function blockUser(userID) {
  const me = getAuth().currentUser?.uid;
  if (!me || !userID || userID === me) return Promise.resolve();
  return set(ref(getDatabase(), `blocks/${me}/${userID}`), serverTimestamp());
}

export function unblockUser(userID) {
  const me = getAuth().currentUser?.uid;
  if (!me) return Promise.resolve();
  return remove(ref(getDatabase(), `blocks/${me}/${userID}`));
}

/* ── Reports ────────────────────────────────────────────────── */

export const REPORT_REASONS = [
  { value: 'no_show',        label: 'No-show' },
  { value: 'unsafe_driving', label: 'Unsafe driving' },
  { value: 'harassment',     label: 'Harassment or inappropriate behavior' },
  { value: 'spam',           label: 'Spam or fake post' },
  { value: 'profile_photo',  label: 'Inappropriate profile photo' },
  { value: 'other',          label: 'Something else' },
];

/** contextType is "ride", "request", "booking" or "passenger" (where the report came from). */
export function sendReport({ reported, reason, note, contextType, contextId }) {
  const me = getAuth().currentUser?.uid;
  if (!me) return Promise.reject(new Error('You need to be logged in.'));
  const report = {
    reporter: me,
    reported,
    reason,
    contextType,
    contextId: String(contextId || ''),
    createdAt: serverTimestamp(),
  };
  const trimmed = (note || '').trim().slice(0, 500);
  if (trimmed) report.note = trimmed;
  return set(push(ref(getDatabase(), 'reports')), report);
}

export function firstName(name) {
  return (name || '').trim().split(/\s+/)[0] || 'this person';
}
